/** Session key set by DeeplinkHandler when Smart Monitor sends ui_capabilities */
export const PARTNER_UI_CAPABILITIES_STORAGE_KEY = 'partner_ui_capabilities';

/** Default = unrestricted iframe UX when SM omits payload (backward compatible). */
const DEFAULT_UI_CAPABILITIES = Object.freeze({
  canViewDeviceInfo: true,
  canViewAlerts: true,
  canViewLocation: true,
  canViewReports: true,
  canViewDeviceSettings: true,
  canEditDeviceSettings: true
});

/** Maps left-nav menu item id -> capability key (partner sessions only). */
const MENU_ID_TO_CAPABILITY = Object.freeze({
  'device-info': 'canViewDeviceInfo',
  'alert-page': 'canViewAlerts',
  'gps-page': 'canViewLocation',
  'health-page': 'canViewDeviceInfo',
  'report-page': 'canViewReports',
  'device-settings-page': 'canViewDeviceSettings'
});

/**
 * Parsed partner UI flags. When not a partner iframe, all booleans are true and isPartnerSession is false.
 */
export function getPartnerUiCapabilities() {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_UI_CAPABILITIES, isPartnerSession: false };
  }
  const isPartnerSession = sessionStorage.getItem('is_partner_session') === 'true';
  if (!isPartnerSession) {
    return { ...DEFAULT_UI_CAPABILITIES, isPartnerSession: false };
  }
  const raw = sessionStorage.getItem(PARTNER_UI_CAPABILITIES_STORAGE_KEY);
  if (!raw) {
    return { ...DEFAULT_UI_CAPABILITIES, isPartnerSession: true, fromSmartMonitorPayload: false };
  }
  try {
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_UI_CAPABILITIES,
      ...parsed,
      isPartnerSession: true,
      fromSmartMonitorPayload: true
    };
  } catch {
    return { ...DEFAULT_UI_CAPABILITIES, isPartnerSession: true, fromSmartMonitorPayload: false };
  }
}

/** Clear persisted capabilities (e.g. on logout / validation failure). */
export function clearPartnerUiCapabilities() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PARTNER_UI_CAPABILITIES_STORAGE_KEY);
}

/** Clear partner deeplink session keys from sessionStorage. */
export function clearPartnerSession() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('device_imei');
  sessionStorage.removeItem('is_partner_session');
  clearPartnerUiCapabilities();
}

/**
 * Restrict nav menu for Smart Monitor iframe when ui_capabilities disables sections.
 * @param {{ items: unknown[] }} menuItems
 */
export function applyPartnerCapabilityFilter(menuItems) {
  const caps = getPartnerUiCapabilities();
  if (!caps.isPartnerSession) {
    return menuItems;
  }
  return {
    items: menuItems.items.map((group) =>
      group.type === 'group' && group.children
        ? {
            ...group,
            children: filterMenuChildren(group.children, caps)
          }
        : group
    )
  };
}

function filterMenuChildren(children, caps) {
  if (!children || !children.length) {
    return children;
  }
  const next = [];
  for (const child of children) {
    const capKey = MENU_ID_TO_CAPABILITY[child.id];
    if (capKey && caps[capKey] === false) {
      continue;
    }
    if (child.type === 'collapse' && child.children) {
      const nested = filterMenuChildren(child.children, caps);
      if (nested.length === 0) continue;
      next.push({ ...child, children: nested });
      continue;
    }
    next.push(child);
  }
  return next;
}

/** Relative path after /:organizationName */
export function getOrgRelativePath(pathname, organizationName) {
  const base = organizationName ? `/${organizationName}` : '';
  if (!pathname.startsWith(base)) return pathname;
  const rel = pathname.slice(base.length) || '/';
  return rel;
}

export function isPartnerPathAllowed(relativePath, caps) {
  if (relativePath.startsWith('/device-info')) return caps.canViewDeviceInfo;
  if (relativePath.startsWith('/alert')) return caps.canViewAlerts;
  if (relativePath.startsWith('/gps')) return caps.canViewLocation;
  if (relativePath.startsWith('/health')) return caps.canViewDeviceInfo;
  if (relativePath.startsWith('/reports')) return caps.canViewReports;
  if (relativePath.startsWith('/usage-reports') || relativePath.startsWith('/admin-stats')) return true;
  if (relativePath.startsWith('/settings')) return caps.canViewDeviceSettings;
  return true;
}

/** @returns {string | null} First allowed path, or null if every flag is false */
export function firstPartnerAllowedRoute(organizationName, caps) {
  const order = [
    ['/device-info', caps.canViewDeviceInfo],
    ['/alert', caps.canViewAlerts],
    ['/gps', caps.canViewLocation],
    ['/health', caps.canViewDeviceInfo],
    ['/reports', caps.canViewReports],
    ['/settings/work-mode-settings', caps.canViewDeviceSettings]
  ];
  for (const [path, ok] of order) {
    if (ok) return `/${organizationName}${path}`;
  }
  return null;
}

/**
 * Partner iframe: SM canEditDeviceSettings AND org extra_info.can_user_modify_settings.
 * Non-partner: org flag only (same as previous app behavior).
 */
export function resolvePartnerModifySettingsAllowed(appUser) {
  if (typeof window === 'undefined') {
    return Boolean(appUser?.organization?.extra_info?.can_user_modify_settings);
  }
  const isPartnerSession = sessionStorage.getItem('is_partner_session') === 'true';
  if (!isPartnerSession) {
    return Boolean(appUser?.organization?.extra_info?.can_user_modify_settings);
  }
  const caps = getPartnerUiCapabilities();
  let partnerUser = {};
  try {
    partnerUser = JSON.parse(sessionStorage.getItem('user') || '{}');
  } catch {
    partnerUser = {};
  }
  const orgAllows = Boolean(partnerUser?.organization?.extra_info?.can_user_modify_settings);
  return Boolean(caps.canEditDeviceSettings && orgAllows);
}
